import {
    BaseDictionary,
    ClientSDK,
    Config,
    RegistrationMode,
    Session,
    Theme,
} from '@easeltv/client-sdk';
import { ok } from '@easeltv/fail';
import { useLocalStorage } from '@easeltv/hooks';
import { error, Logger, setMinLogLevel } from '@easeltv/logger';
import { useEffect, useRef, useState } from 'react';

import { useErrorReport } from '../ErrorProvider/ErrorProvider';

/* -------------------------------- Constants ------------------------------- */

export const TENANT_CODE =
    window.FHW_TENANT_CODE && window.FHW_TENANT_CODE !== '{{tenantCode}}'
        ? window.FHW_TENANT_CODE
        : import.meta.env.VITE_FALLBACK_TENANT ?? 'etv';

const GENERIC_ERROR = {
    title: 'Something went wrong',
    body: 'Sorry, there appears to be an issue.',
};

/* --------------------------------- Logging -------------------------------- */

if (import.meta.env.DEV || import.meta.env.VITE_API_ENV === 'dev') {
    Logger().registerProvider({ type: 'console' });
} else if (!import.meta.env.DEV && import.meta.env.VITE_SENTRY_CW_DSN) {
    setMinLogLevel('error');
    Logger().registerProvider({
        type: 'sentry',
        dsn: import.meta.env.VITE_SENTRY_CW_DSN,
        release: import.meta.env.VITE_VERSION_TAG,
        tenant: TENANT_CODE,
        environment: import.meta.env.VITE_API_ENV,
    });
}

/* ---------------------------------- Types --------------------------------- */

/**
 * ApplicationData
 *
 * Data stored in the ApplicationProvider
 */
export interface ApplicationContextI {
    /** Instance of the ClientSDK */
    api: ClientSDK;

    /** Config response from the API */
    config: Config;

    /** Dictionary response from the API */
    dictionary: BaseDictionary;

    /** Session response from the API */
    session: Session | undefined;

    /** Theme response from the API */
    theme: Theme;
}

/* --------------------------------- Helpers -------------------------------- */

/**
 * useInitializeApplication()
 *
 * Initializes the ClientSDK, and makes all the calls necessary for the
 * application to start.
 *
 * @returns
 */
export const useInitializeApplication = () => {
    const { fatal, setErrorTheme } = useErrorReport();

    const [data, setData] = useState<ApplicationContextI>();
    const [loading, setLoading] = useState(true);

    const [restoredCache] = useLocalStorage<string | undefined>(
        'easel.cache',
        undefined,
    );

    const [, setRefreshToken, getRefreshToken] = useLocalStorage<
        string | undefined
    >('refreshToken', undefined);

    const mounted = useRef(false);

    const init = useRef(async () => {
        if (!mounted.current) {
            // Instantiate a new SDK instance.
            const api = new ClientSDK({
                tenant: TENANT_CODE,
                env: import.meta.env.VITE_API_ENV,
                refreshTokenStorage: {
                    get: getRefreshToken,
                    set: setRefreshToken,
                },
                restoredCache,
                logger: Logger(),
            });

            // Initialize a session using the refreshToken if it exists, also fetch the config so it can be cached, if
            // necessary, for future request
            const configResult = await api.getConfig();

            if (!configResult.ok) throw Error();

            const config = configResult.payload.body;

            // Fetch the remaining blocking resources simultaneously.
            const [sessionResult, dictionaryResult] = await Promise.all([
                config.registration.mode === RegistrationMode.InviteOnly
                    ? Promise.resolve(ok({ status: 200, body: undefined }))
                    : api.initialize(),

                api.getDictionary('en'),
            ]);

            // Any errors here should probably be Epic.
            if (!sessionResult.ok || !dictionaryResult.ok) {
                fatal({
                    title:
                        (dictionaryResult.ok &&
                            dictionaryResult?.payload?.body?.definitions?.[
                                'common.error.title'
                            ]) ||
                        GENERIC_ERROR.title,
                    body:
                        (dictionaryResult.ok &&
                            dictionaryResult?.payload?.body?.definitions?.[
                                'common.error.generic'
                            ]) ||
                        GENERIC_ERROR.body,
                });

                error({ configResult, dictionaryResult });

                return;
            }

            const themeResult = await api.getTheme();

            if (themeResult.ok) {
                setErrorTheme(themeResult.payload.body);
            }

            if (!themeResult.ok) {
                fatal({
                    title:
                        dictionaryResult.payload.body.definitions?.[
                            'common.error.title'
                        ] || GENERIC_ERROR.title,
                    body:
                        dictionaryResult.payload.body.definitions?.[
                            'common.error.generic'
                        ] || GENERIC_ERROR.body,
                });

                error({ configResult, dictionaryResult });

                return;
            }

            if (config.registration.mode !== RegistrationMode.InviteOnly)
                api.preloadLabels();

            const theme = themeResult.payload.body;
            const dictionary = dictionaryResult.payload.body;
            const session = sessionResult.payload.body;

            if (import.meta.env.DEV) {
                window.api = api;

                window.config = config;
                window.dictionary = dictionary;
                window.theme = theme;
            }

            setData({
                api,
                config,
                dictionary,
                session,
                theme,
            });
        }

        setLoading(false);
    });

    useEffect(() => {
        init.current();

        return () => {
            mounted.current = true;
        };
    }, []);

    // Return data.
    return { loading, data };
};
